import { NewRuleDefinitionResponse } from 'app/graphql/generated/graphqlApolloTypes';

import {
  CollectionFilter,
  defaultCustomHierarchyFilter,
  HierarchyType,
  MapCustomHierarchySetting,
  MapRuleFields,
  MchQuantity,
  MchQuantityType,
  NamedRootHierarchy,
  RootHierarchyField,
  RuleForMap,
  Segment
} from 'app/models';

import { isEmptyLegacyFilter, passesSegmentFilter } from 'utils/helpers/segmentFilterUtils';
import {
  areChosenCustomsEqual,
  isDefaultFilter,
  isMappableCustom,
  passesCollectionFilter,
  ReadonlyMapOrSet
} from 'utils/helpers/territoryMapUtils';

import { GeoId, AccountId, RuleId, CustomId } from './MapActorV2';
import { PrimaryHierarchyTypeForMap } from './MapWorkerProtocol';

export type RuleForLookup = {
  rule: MapRuleFields;
  definition: NewRuleDefinitionResponse;
};

export interface RulesAndSettings {
  chosenCustomHierarchy: MapCustomHierarchySetting;
  primaryHierarchy: PrimaryHierarchyTypeForMap;
  rules: ReadonlyArray<RuleForLookup>;
  additionalCustomHierarchies: { has: (customHierarchyId: CustomId) => boolean };
}

export class RuleLookups {
  private readonly overAssignedGeosCount = new Map<GeoId, number>();
  private readonly overAssignedAccountsCount = new Map<AccountId, number>();

  private readonly geoBaseAssignments = new Map<GeoId, RuleId>();
  private readonly accountBaseAssignments = new Map<AccountId, RuleId>();

  private readonly accountOverrideAssignments = new Map<AccountId, RuleId>();
  private readonly reservedAccountIds = new Set<AccountId>();

  private readonly rules = new Map<RuleId, RuleForMap>();
  private readonly ruleComponents = new Map<RuleId, MappableRuleComponents>();
  private readonly customsInRules = new Map<CustomId, NamedRootHierarchy>();

  // Can be re-assigned in the case that only the kind changed
  private filter: CollectionFilter<number>[];

  private constructor(
    private readonly init: RulesAndSettings,
    desiredFilter: CollectionFilter<number>[]
  ) {
    const rulesWithComponents = this.init.rules.map((rule) => ({
      rule: RuleLookups.parseRuleFields(rule),
      components: RuleLookups.parseRuleComponents(rule, this.init.primaryHierarchy, this.init.chosenCustomHierarchy)
    }));

    for (const {
      components: { customHierarchies }
    } of rulesWithComponents) {
      for (const customHierarchy of customHierarchies.values()) {
        this.customsInRules.set(customHierarchy.customHierarchyId, customHierarchy);
      }
    }

    const orphanedCustomHierarchies = this.getOrphanedCustomHierarchies(desiredFilter, this.customsInRules);

    this.filter = RuleLookups.calculateNextFilter(
      desiredFilter,
      (id) => this.customsInRules.has(id) || this.init.additionalCustomHierarchies.has(id),
      init.chosenCustomHierarchy.quantity
    );
    const isPassingFilter = this.createIsPassingFilterCheck();
    const rulesPassingFilter = new Set<RuleId>();
    const overAssignedRules = new Set<RuleId>();

    rulesWithComponents.forEach(
      ({
        rule,
        components: { geoHierarchyIds, accountHierarchyIds, overrideAccountHierarchyIds, isMappable, customHierarchies }
      }) => {
        if (isPassingFilter(customHierarchies, orphanedCustomHierarchies)) {
          rulesPassingFilter.add(rule.ruleId);
        } else {
          for (const accountId of overrideAccountHierarchyIds) this.reservedAccountIds.add(accountId);
          return;
        }

        if (!isMappable) {
          for (const accountId of overrideAccountHierarchyIds) this.reservedAccountIds.add(accountId);
          return;
        }

        for (const overrideAccountId of overrideAccountHierarchyIds) {
          this.accountOverrideAssignments.set(overrideAccountId, rule.ruleId);
        }

        RuleLookups.parseBaseAssignmentsAndCollisions(
          geoHierarchyIds,
          rule.ruleId,
          this.geoBaseAssignments,
          this.overAssignedGeosCount,
          overAssignedRules
        );
        RuleLookups.parseBaseAssignmentsAndCollisions(
          accountHierarchyIds,
          rule.ruleId,
          this.accountBaseAssignments,
          this.overAssignedAccountsCount,
          overAssignedRules
        );
      }
    );

    rulesWithComponents.forEach(({ rule, components }) => {
      this.rules.set(rule.ruleId, {
        ...rule,
        isMappable: components.isMappable,
        isEmpty: components.isEmpty,
        isPassingFilter: rulesPassingFilter.has(rule.ruleId),
        isOverAssigned: overAssignedRules.has(rule.ruleId),
        accountModifierCount: components.overrideAccountHierarchyIds.size
      });
      this.ruleComponents.set(rule.ruleId, components);
    });
  }

  public getTerritoryCountsForSegments(segments: ReadonlyArray<Pick<Segment, 'segmentId' | 'clauses'>>) {
    const counts = new Map<Segment['segmentId'], number>();
    for (const segment of segments) {
      const segmentId = segment.segmentId;
      counts.set(segmentId, 0);
      for (const { customHierarchies } of this.ruleComponents.values()) {
        if (passesSegmentFilter(segment, customHierarchies)) counts.set(segmentId, counts.get(segmentId) + 1);
      }
    }
    return counts;
  }

  public static createInitialRuleLookups(): RuleLookups {
    return new RuleLookups(
      {
        chosenCustomHierarchy: { quantity: MchQuantity.NONE },
        primaryHierarchy: HierarchyType.GeographicTerritoryHierarchy,
        rules: [],
        additionalCustomHierarchies: new Set()
      },
      defaultCustomHierarchyFilter
    );
  }

  public createNextRuleLookupsForRuleChange(init: RulesAndSettings): RuleLookups {
    const desiredFilter = areChosenCustomsEqual(init.chosenCustomHierarchy, this.init.chosenCustomHierarchy)
      ? this.filter
      : defaultCustomHierarchyFilter;
    return new RuleLookups(init, desiredFilter);
  }

  public createNextRuleLookupsForFilterChange(filter: CollectionFilter<number>[]): RuleLookups {
    return new RuleLookups(this.init, filter);
  }

  public mutateFilterKind([nextFilterClause]: CollectionFilter<number>[]) {
    this.filter = [{ rootHierarchyId: nextFilterClause.rootHierarchyId, kind: nextFilterClause.kind, ids: [] }];
  }

  private static parseBaseAssignmentsAndCollisions<HierarchyId extends GeoId | AccountId>(
    hierarchyIds: Iterable<HierarchyId>,
    ruleId: RuleId,
    assignmentMap: Map<HierarchyId, RuleId>,
    overAssignmentCountMap: Map<HierarchyId, number>,
    overAssignedRules: Set<RuleId>
  ) {
    for (const hierarchyId of hierarchyIds) {
      // Pre-existing collision
      const existingCollisionCount = overAssignmentCountMap.get(hierarchyId) ?? 0;
      if (existingCollisionCount) {
        overAssignedRules.add(ruleId);
        overAssignmentCountMap.set(hierarchyId, existingCollisionCount + 1);
        continue;
      }

      // First time collision
      const collidingRuleId = assignmentMap.get(hierarchyId);
      if (collidingRuleId) {
        assignmentMap.delete(hierarchyId);
        overAssignedRules.add(collidingRuleId);
        overAssignedRules.add(ruleId);
        overAssignmentCountMap.set(hierarchyId, 2);
        continue;
      }

      assignmentMap.set(hierarchyId, ruleId);
    }
  }

  private static parseRuleFields({ rule }: RuleForLookup): MapRuleFields {
    const {
      ruleId,
      territoryId,
      territoryName,
      territoryGroupId,
      effectiveDate,
      endDate,
      color,
      numberOfAccounts,
      measureValue
    } = rule;
    return {
      ruleId,
      territoryId,
      territoryName,
      territoryGroupId,
      effectiveDate,
      endDate,
      color,
      numberOfAccounts,
      measureValue
    };
  }

  private getOrphanedCustomHierarchies(
    filter: CollectionFilter<number>[],
    customHierarchiesMappedToRoot: ReadonlyMap<number, NamedRootHierarchy>
  ): { customHierarchyId: number; rootCustomHierarchyId: number }[] {
    const orphanedCustomHierarchies = [];

    filter.forEach((currentFilter) => {
      currentFilter.ids.forEach((customHierarchyId) => {
        if (!customHierarchiesMappedToRoot.get(customHierarchyId)) {
          orphanedCustomHierarchies.push({ customHierarchyId, rootCustomHierarchyId: currentFilter.rootHierarchyId });
        }
      });
    });

    return orphanedCustomHierarchies;
  }

  private static parseRuleComponents(
    rule: RuleForLookup,
    primaryHierarchy: PrimaryHierarchyTypeForMap,
    chosenCustomHierarchy: MapCustomHierarchySetting
  ): MappableRuleComponents {
    const {
      // prettier-ignore
      hasForbiddenOverrides,
      overrideAccountHierarchyIds
    } = RuleLookups.parseModifiers(rule.definition.modifiers?.inclusions);
    const {
      // prettier-ignore
      hasForbiddenInclusions,
      geoHierarchyIds,
      accountHierarchyIds,
      customHierarchies
    } = RuleLookups.parseBaseInclusions(rule.definition.base?.inclusions, chosenCustomHierarchy);
    const {
      // prettier-ignore
      hasForbiddenExclusions
    } = RuleLookups.parseBaseExclusions(rule.definition.base?.exclusions, chosenCustomHierarchy);

    const hasForbiddenComponents = hasForbiddenOverrides || hasForbiddenInclusions || hasForbiddenExclusions;

    let isMappable: boolean;
    let isEmpty: boolean;
    switch (primaryHierarchy) {
      case HierarchyType.GeographicTerritoryHierarchy:
        isEmpty = geoHierarchyIds.length === 0;
        isMappable = !hasForbiddenComponents && accountHierarchyIds.length === 0;
        break;
      case HierarchyType.CustomerAccountHierarchy:
        isEmpty = accountHierarchyIds.length === 0;
        isMappable = !hasForbiddenComponents && geoHierarchyIds.length === 0 && overrideAccountHierarchyIds.size === 0;
        break;

      default:
        throw new Error(`Unexpected primary hierarchy ${primaryHierarchy}`);
    }

    return {
      geoHierarchyIds,
      customHierarchies,
      accountHierarchyIds,
      overrideAccountHierarchyIds,
      isEmpty,
      isMappable
    };
  }

  private static parseModifiers(
    modifierInclusions: RuleForLookup['definition']['modifiers']['inclusions'] | undefined
  ) {
    let hasForbiddenOverrides = false;
    const overrideAccountHierarchyIds = new Set<AccountId>();
    for (const modInc of modifierInclusions ?? []) {
      switch (modInc.hierarchyType) {
        case RootHierarchyField.CustomerAccountHierarchyField:
          for (const { hierarchyId } of modInc.contains) overrideAccountHierarchyIds.add(hierarchyId);
          break;
        default:
          hasForbiddenOverrides = true;
          break;
      }
    }
    return { overrideAccountHierarchyIds, hasForbiddenOverrides };
  }

  private static parseBaseInclusions(
    baseInclusions: RuleForLookup['definition']['base']['inclusions'] | undefined,
    chosenCustomHierarchy: MapCustomHierarchySetting
  ) {
    let hasForbiddenInclusions = false;
    const geoHierarchyIds = new Array<GeoId>();
    const accountHierarchyIds = new Array<AccountId>();
    const customHierarchies = new Map<CustomId, NamedRootHierarchy>();
    for (const baseInc of baseInclusions ?? []) {
      switch (baseInc.hierarchyType) {
        case RootHierarchyField.GeographicTerritoryHierarchyField:
          for (const { hierarchyId } of baseInc.contains) geoHierarchyIds.push(hierarchyId);
          break;
        case RootHierarchyField.CustomerAccountHierarchyField:
          for (const { hierarchyId } of baseInc.contains) accountHierarchyIds.push(hierarchyId);
          break;
        case RootHierarchyField.CustomHierarchyField:
          if (isMappableCustom(chosenCustomHierarchy, baseInc)) {
            for (const custom of baseInc.contains) {
              customHierarchies.set(custom.hierarchyId, {
                rootHierarchyId: baseInc.rootHierarchyId,
                rootHierarchyName: baseInc.rootHierarchyName,
                customHierarchyId: custom.hierarchyId,
                customHierarchyName: custom.name
              } as NamedRootHierarchy);
            }
          } else {
            hasForbiddenInclusions = true;
          }
          break;
        default:
          hasForbiddenInclusions = true;
          break;
      }
    }

    return {
      hasForbiddenInclusions,
      geoHierarchyIds,
      accountHierarchyIds,
      customHierarchies
    };
  }

  private static parseBaseExclusions(
    exclusions: RuleForLookup['definition']['base']['exclusions'] | undefined,
    chosenCustomHierarchy: MapCustomHierarchySetting
  ) {
    let hasForbiddenExclusions = false;
    for (const baseExc of exclusions ?? []) {
      switch (baseExc.hierarchyType) {
        case RootHierarchyField.CustomHierarchyField:
          if (!isMappableCustom(chosenCustomHierarchy, baseExc)) {
            hasForbiddenExclusions = true;
          }
          break;
        default:
          hasForbiddenExclusions = true;
          break;
      }
    }
    return { hasForbiddenExclusions };
  }

  public hasRules() {
    return this.init.rules.length > 0;
  }

  private static calculateNextFilter(
    desiredFilter: CollectionFilter<number>[],
    isIdAvailable: (id: CustomId) => boolean,
    quantity: MchQuantityType
  ): CollectionFilter<number>[] {
    const resultFilter = desiredFilter.map((filter) => {
      return {
        rootHierarchyId: filter.rootHierarchyId,
        kind: filter.kind,
        ids: quantity === MchQuantity.ANY ? filter.ids : filter.ids.filter(isIdAvailable)
      };
    });

    return resultFilter;
  }

  public getAllAssignedGeos() {
    return [...this.geoBaseAssignments.keys(), ...this.overAssignedGeosCount.keys()];
  }

  private isNoneFilterPassing(
    customIds: ReadonlyMap<number, NamedRootHierarchy>,
    rootHierarchiesInFilter: Set<number>
  ) {
    if (this.init.chosenCustomHierarchy.quantity === MchQuantity.ANY) {
      for (const [_key, value] of customIds) {
        const rootHierarchyId = value.rootHierarchyId;

        const containsRootHierarchy = rootHierarchiesInFilter.has(rootHierarchyId);

        if (!containsRootHierarchy) return false;
      }
    }
    return true;
  }

  private createIsPassingFilterCheck() {
    if (this.init.chosenCustomHierarchy.quantity === MchQuantity.NONE) {
      return () => true;
    }
    if (isEmptyLegacyFilter(this.filter)) {
      return () => true;
    }
    // Indicates no-custom hierarchies should be present
    if (this.filter.length === 0) {
      return (customIds: ReadonlyMapOrSet<CustomId> | undefined) => customIds.size === 0;
    }

    if (isDefaultFilter(this.filter)) return () => false;

    const rootHierarchiesInFilter = new Set<number>();
    this.filter.forEach((filter) => rootHierarchiesInFilter.add(filter.rootHierarchyId));

    return (
      customIds: ReadonlyMap<number, NamedRootHierarchy> | undefined,
      orphanedCustomHierarchies: { customHierarchyId: number; rootCustomHierarchyId: number }[]
    ) => {
      if (!this.isNoneFilterPassing(customIds, rootHierarchiesInFilter)) return false;

      for (const filter of this.filter) {
        const relevantCustomIds = new Map<number, NamedRootHierarchy>();

        for (const [key, value] of customIds) {
          const rootHierarchyId = this.customsInRules.get(key).rootHierarchyId;
          if (rootHierarchyId === filter.rootHierarchyId || !filter.rootHierarchyId) {
            relevantCustomIds.set(key, value);
          }
        }

        for (const orphanedCustomHierarchy of orphanedCustomHierarchies) {
          if (orphanedCustomHierarchy.rootCustomHierarchyId === filter.rootHierarchyId) {
            relevantCustomIds.set(orphanedCustomHierarchy.customHierarchyId, null);
          }
        }
        orphanedCustomHierarchies.forEach((orphanedCustomHierarchy) => {
          if (orphanedCustomHierarchy.rootCustomHierarchyId === filter.rootHierarchyId) {
            relevantCustomIds.set(orphanedCustomHierarchy.customHierarchyId, null);
          }
        });

        if (!passesCollectionFilter(filter, relevantCustomIds)) return false;
      }
      return true;
    };
  }

  public getFilter() {
    return this.filter;
  }

  public getAllRules() {
    return this.rules.values();
  }

  public getRule(ruleId: RuleId) {
    return this.rules.get(ruleId);
  }

  public getCustomHierarchiesInRule(ruleId) {
    return this.ruleComponents.get(ruleId).customHierarchies;
  }

  public isRuleOverAssigned(ruleId: RuleId): boolean {
    return this.rules.get(ruleId).isOverAssigned;
  }

  public getAllOverAssignedGeoCounts() {
    return this.overAssignedGeosCount.entries();
  }

  public getAllCustomHierarchiesInRules(): ReadonlyMap<number, NamedRootHierarchy> {
    return this.customsInRules;
  }

  public getAllGeosReferencedInRule(ruleId: number) {
    if (this.init.primaryHierarchy === HierarchyType.CustomerAccountHierarchy) return [];
    const rule = this.rules.get(ruleId);
    if (!rule?.isMappable) return [];
    return this.ruleComponents.get(ruleId).geoHierarchyIds;
  }

  public getPrimaryHierarchyType(): PrimaryHierarchyTypeForMap {
    return this.init.primaryHierarchy;
  }

  public hasAccountOverride(accountId: AccountId) {
    return this.accountOverrideAssignments.has(accountId);
  }

  public calculateAccountAssignment(accountId: AccountId, geographyId: GeoId): RuleId | null {
    if (this.accountOverrideAssignments.has(accountId)) return this.accountOverrideAssignments.get(accountId);
    if (this.accountBaseAssignments.has(accountId)) return this.accountBaseAssignments.get(accountId);
    if (this.geoBaseAssignments.has(geographyId)) return this.geoBaseAssignments.get(geographyId);
    return null;
  }

  public isAccountReserved(accountId: AccountId) {
    return this.reservedAccountIds.has(accountId);
  }

  public getOverassignedGeoCount(geoId: GeoId): number {
    return this.overAssignedGeosCount.get(geoId) ?? 0;
  }

  public isGeoOverassigned(geoId: GeoId): boolean {
    return this.overAssignedGeosCount.has(geoId);
  }

  public getGeoAssignment(geoId: GeoId): RuleId | undefined {
    return this.geoBaseAssignments.get(geoId);
  }

  public isGeoAssigned(geoId: GeoId) {
    return this.geoBaseAssignments.has(geoId);
  }

  public getOverassignedAccountCount(accountId: AccountId): number {
    return this.overAssignedAccountsCount.get(accountId) ?? 0;
  }

  public getAllRuleIds() {
    return this.rules.keys();
  }

  public getNamedHierarchiesFromFilter(): NamedRootHierarchy[] {
    const namedHierarchies = [];

    this.filter.forEach((filter) => {
      namedHierarchies.push(...filter.ids.map((id) => this.customsInRules.get(id)));
    });

    return namedHierarchies;
  }

  public getOverAssignedAccountsForRule(selectedRuleId: RuleId): AccountId[] {
    return this.ruleComponents
      .get(selectedRuleId)
      .accountHierarchyIds.filter((accountId) => this.overAssignedAccountsCount.has(accountId));
  }

  public getOverAssignedGeosForRule(selectedRuleId: RuleId): GeoId[] {
    return this.getAllGeosReferencedInRule(selectedRuleId).filter((geoId) => this.isGeoOverassigned(geoId));
  }

  public getRuleCount() {
    return this.rules.size;
  }

  public getCustomHierarchyQuantity() {
    return this.init.chosenCustomHierarchy.quantity;
  }

  public getCustomRootHierarchyId() {
    if (this.init.chosenCustomHierarchy.quantity !== 'SINGULAR') return null;
    return this.init.chosenCustomHierarchy.details.rootHierarchyId;
  }
}

export interface MappableRuleComponents {
  geoHierarchyIds: ReadonlyArray<GeoId>;
  customHierarchies: ReadonlyMap<CustomId, NamedRootHierarchy>;
  accountHierarchyIds: ReadonlyArray<AccountId>;
  overrideAccountHierarchyIds: ReadonlySet<AccountId>;
  isMappable: boolean;
  isEmpty: boolean;
}
